// @givmo/passkeys — the "Confirm it's you" sheet's words for adding and removing a passkey
// (AS-WEB11), and how the factor rules' refusals find them.
//
// Every sentence is approved: the add sheet's bodies are D-AS-29's fix 10 (with and without an
// authenticator app) and fix 14 (an account that already holds a passkey), the last-passkey body is
// fix 14's, the one-of-several removal and its foot are D-AS-21's B1, and the add sheets' foot is
// D-AS-35's fix 8. Each act's refusal names
// its row by its code and `context.action`, because the SAME act (`add_passkey`) is refused under
// either code depending on the account (backend rule 1), and the authenticator variant depends on
// the person, which only the host knows.
//
// SEC2-AS2 H1-C (backend cut 1005a): a phone or employer sign-in adds an account's FIRST passkey
// only when it is the account's only way to sign in, refused otherwise under its own code,
// `first_passkey_other_sign_in_required`, so the shipped apps (whose re-sign-in defaults to the
// session's own provider, the phone) render its line instead. Here it opens the first-passkey sheet:
// a console's ways in are the account's own password and providers, never a phone or an employer's
// sign-in, and re-signing in with any of them clears it.

import {
  PASSKEY_CONFIRMATION_REQUIRED_ERROR_CODE,
  STEP_UP_REQUIRED_ERROR_CODE,
  stepUpActionOf,
  type StepUpCopyTable,
  type StepUpRefusalCodes,
} from '@givmo/step-up';

/**
 * The backend's refusal of a first passkey on a phone or employer sign-in when the account has
 * another way in (SEC2-AS2 H1-C), or when the backend could not read its ways in.
 */
export const FIRST_PASSKEY_OTHER_SIGN_IN_REQUIRED_ERROR_CODE =
  'first_passkey_other_sign_in_required';

/** The rows' keys. */
export const PASSKEY_STEP_UP_ROWS = {
  /** Adding a first passkey on a sign-in that is not recent enough (no authenticator). */
  addAfterSignIn: 'passkey_add_recent_sign_in',
  /** Adding a first passkey on an account with an authenticator app: the sign-in must answer it. */
  addAfterAuthenticator: 'passkey_add_authenticator',
  /** Adding another passkey: only a passkey already on the account clears it. */
  addWithPasskey: 'passkey_add_confirm_passkey',
  /** Removing one of several (the 30-minute step-up). */
  removeOne: 'passkey_remove',
  /** Removing the last: only that passkey clears it. */
  removeLast: 'passkey_remove_last',
} as const;

const REMOVE_CONSEQUENCE = 'Nothing is removed until you confirm.';
/** D-AS-35 fix 8: the three add sheets' foot, as the removal sheets carry theirs. */
const ADD_CONSEQUENCE = 'Nothing is added until you confirm.';

/** The rows, with the passkey being removed named where B1's line names it. */
export function passkeyStepUpCopy(
  removing: { name: string | null } | null = null,
): StepUpCopyTable {
  const name = removing?.name?.trim() ?? '';
  return {
    [PASSKEY_STEP_UP_ROWS.addAfterSignIn]: {
      description: () =>
        'Adding a passkey needs a recent sign-in. Confirm it’s you and we’ll add it.',
      consequence: ADD_CONSEQUENCE,
    },
    [PASSKEY_STEP_UP_ROWS.addAfterAuthenticator]: {
      description: () =>
        'Adding a passkey needs a sign-in with your authenticator app. Confirm it’s you and we’ll add it.',
      consequence: ADD_CONSEQUENCE,
    },
    [PASSKEY_STEP_UP_ROWS.addWithPasskey]: {
      description: () =>
        'Your account already has a passkey, so confirm it’s you with one of your passkeys first. We’ll add the new one right after.',
      consequence: ADD_CONSEQUENCE,
    },
    [PASSKEY_STEP_UP_ROWS.removeOne]: {
      description: () =>
        name.length > 0
          ? `Givmo asks everyone to have signed in within the last 30 minutes before removing a passkey. The one on ${name} is removed as soon as you confirm.`
          : 'Givmo asks everyone to have signed in within the last 30 minutes before removing a passkey.',
      consequence: REMOVE_CONSEQUENCE,
    },
    [PASSKEY_STEP_UP_ROWS.removeLast]: {
      description: () =>
        'This is the last passkey on your account, so confirm it’s you with it first. We’ll remove it right after.',
      consequence: REMOVE_CONSEQUENCE,
    },
  };
}

/**
 * The factor rules' refusals, mapped to their rows. `holdsAuthenticator` picks the first-passkey
 * body (the host's `GET /users/me/security` fact). A refusal for any other act reads its own
 * `context.action`, as before.
 */
export function passkeyRefusalCodes(holdsAuthenticator: boolean | undefined): StepUpRefusalCodes {
  return {
    [STEP_UP_REQUIRED_ERROR_CODE]: (error) => {
      const action = stepUpActionOf(error);
      if (action === 'add_passkey') {
        return holdsAuthenticator === true
          ? PASSKEY_STEP_UP_ROWS.addAfterAuthenticator
          : PASSKEY_STEP_UP_ROWS.addAfterSignIn;
      }
      if (action === 'delete_passkey') return PASSKEY_STEP_UP_ROWS.removeOne;
      return undefined;
    },
    [PASSKEY_CONFIRMATION_REQUIRED_ERROR_CODE]: (error) => {
      const action = stepUpActionOf(error);
      if (action === 'add_passkey') return PASSKEY_STEP_UP_ROWS.addWithPasskey;
      if (action === 'delete_passkey') return PASSKEY_STEP_UP_ROWS.removeLast;
      return undefined;
    },
    // A re-sign-in by any of the account's own ways clears it; the account holds no passkey here.
    [FIRST_PASSKEY_OTHER_SIGN_IN_REQUIRED_ERROR_CODE]: () => ({
      row: PASSKEY_STEP_UP_ROWS.addAfterSignIn,
      accepts: ['password', 'google', 'apple', 'microsoft'],
    }),
  };
}
